import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { Divider, Flex, Menu, Stack, Text } from '@mantine/core';
import { MouseEventHandler, useState } from 'react';
import { ValuationsValidationError } from '@dealstack/frontend/app/pages/Valuations/components/ValuationsWarnings/ValuationsWarnings.utils';
import {
	ValuationsWarningButton,
	ValuationsWarningIconButton,
	ValuationsWarningsDropdownHeader,
} from '@dealstack/frontend/app/pages/Valuations/components/ValuationsWarnings/ValuationsWarnings.styled';
import { MODAL_WIDTH } from '@dealstack/frontend/app/pages/Valuations/components/ValuationsWarnings/ValuationsWarnings.constants';

interface Props {
	errors: ValuationsValidationError[];
	navigateToError: (error: ValuationsValidationError) => void;
}

function ValuationsWarningsListItem({
	errorTitle,
	errorDescription,
	onShowClick,
	onHideClick,
}: {
	errorTitle: string;
	errorDescription: string;
	onShowClick: MouseEventHandler<HTMLButtonElement>;
	onHideClick: MouseEventHandler<HTMLButtonElement>;
}) {
	const tValuations = useTranslationValuations();

	return (
		<Flex direction="column" gap={8}>
			<Flex direction="column" gap={4}>
				<Text size="B6Bold">{errorTitle}</Text>
				<Text size="B6Medium" c={defaultTheme.v3.colors.dark[0]}>
					{errorDescription}
				</Text>
			</Flex>
			<Flex justify="space-between">
				<ValuationsWarningButton onClick={onShowClick} variant="subtle">
					<Text size="B6Bold" c={defaultTheme.v3.colors.blue[6]}>
						{tValuations('valuations_error.dropdown_item_navigate_to_cause')}
					</Text>
				</ValuationsWarningButton>
				<ValuationsWarningButton onClick={onHideClick} variant="subtle">
					<Text size="B6Bold" c={defaultTheme.v3.colors.black[0]}>
						{tValuations('valuations_error.dropdown_item_hide_error')}
					</Text>
				</ValuationsWarningButton>
			</Flex>
		</Flex>
	);
}

export function ValuationsValidationWarningsMenu({ errors: allErrors, navigateToError }: Props) {
	const [opened, setOpened] = useState(false);
	const [visibleErrors, setVisibleErrors] = useState(allErrors);
	const [previousAllErrors, setPreviousAllErrors] = useState(allErrors);
	const tValuations = useTranslationValuations();

	// Reset state when previousAllErrors changes
	if (previousAllErrors !== allErrors) {
		setVisibleErrors(allErrors);
		setPreviousAllErrors(allErrors);
	}

	const hasHiddenErrors = visibleErrors.length !== allErrors.length;
	const hasVisibleErrors = allErrors.length > 0 && visibleErrors.length !== 0;

	return (
		<Menu
			width={MODAL_WIDTH}
			opened={opened}
			onClose={() => setOpened(false)}
			shadow="md"
			position="bottom-end"
		>
			<Menu.Target>
				<ValuationsWarningIconButton
					variant="subtle"
					color="red"
					onClick={() => setOpened(!opened)}
				>
					<Icon variant="warning" size="vlg" />
				</ValuationsWarningIconButton>
			</Menu.Target>
			<Menu.Dropdown
				p="0"
				style={{
					border: '1px solid',
					borderColor: defaultTheme.v3.colors.gray[0],
					borderRadius: '0.5rem',
					boxShadow: '0px 16px 32px 0px #DCDFE0',
					padding: '16px',
				}}
			>
				<Stack gap={10}>
					<Flex>
						<ValuationsWarningsDropdownHeader />
					</Flex>
					{visibleErrors.map(
						(
							{ personId, mipId, title: errorTitle, description: errorDescription },
							index
						) => {
							return (
								<div key={mipId ? `${personId}-${mipId}` : `${personId}`}>
									<ValuationsWarningsListItem
										errorTitle={tValuations(errorTitle)}
										errorDescription={tValuations(errorDescription)}
										onShowClick={() => navigateToError(visibleErrors[index])}
										onHideClick={() =>
											setVisibleErrors((errors) =>
												errors.filter(
													({ personId: pId, mipId: gId }) =>
														!(pId === personId && mipId === gId)
												)
											)
										}
									/>
									{index !== visibleErrors.length - 1 && (
										<Divider
											color={defaultTheme.v3.colors.gray[2]}
											mt={10}
											mb={10}
										/>
									)}
								</div>
							);
						}
					)}
					{hasHiddenErrors && hasVisibleErrors && (
						<Divider color={defaultTheme.v3.colors.gray[2]} mt={10} mb={10} />
					)}
					{hasHiddenErrors && (
						<Flex justify="center" align="center">
							<Text
								size="B6Medium"
								onClick={() => setVisibleErrors(allErrors)}
								style={{ cursor: 'pointer' }}
							>
								{tValuations('valuations_error.show_hidden_errors')}
							</Text>
						</Flex>
					)}
				</Stack>
			</Menu.Dropdown>
		</Menu>
	);
}
